import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  Popover,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { StyledContainerRoot } from '../../Assignments/AssignmentsTable/List.styled';
import { ClassesSort, PaginationProps } from '../../../variables/types';
import CustomPagination from '../../../components/CustomPagination';
import NoResults from '../../../components/NoResults';
import WavingLadyFrame from '../../../components/Icons/WavingLadyFrame';
import { OrderBy, SectionCategory, SectionDetailsFragmentFragment, TeacherInfo } from '../../../generated/graphql';
import ArrowDownIcon from '../../../components/Icons/ArrowDownIcon';
import SuccessIcon from '../../../components/Icons/SuccessIcon';
import {
  StyledTableCell,
  StyledTableCellOverFlowContent,
  StyledTableDataCell,
  StyledTableDataCellWidth10,
  StyledTableDataColumn,
} from '../../Admin/admin-tables-styled';
import { StyledTableSort, tableWithNoDataStyles } from '../../../components/Common/Common.styled';
import { palette  } from '../../../theme/palette';
import useRole from '../../../hooks/useRole';

interface Props {
  isTableEmpty: boolean;
  pagination: PaginationProps,
  loading: boolean,
  totalCount: number,
  sort: ClassesSort,
  order: OrderBy,
  classes?: SectionDetailsFragmentFragment[],
  handlePageChange: (_: React.ChangeEvent<unknown>,
    value: React.SetStateAction<number>) => void | undefined,
  handleSort: ((column: ClassesSort) => void) | Function,
  marginTop?: number,
  sxProps?: SxProps,
  rowsPerPage: number,
  setRowsPerPage: Function,
}

const successIconStyles = {
  customClassIcon: {
    color: palette.customBlue.cornflowerBlue,
    '& circle': {
      fill: palette.customBlue.cornflowerBlue,
    },
  },
};

const List = ({
  isTableEmpty,
  pagination,
  loading,
  totalCount,
  sort,
  order,
  handlePageChange,
  classes: classesData,
  handleSort,
  marginTop = 2, sxProps,
  rowsPerPage,
  setRowsPerPage,
}: Props) => {
  const orderByIcon = order === OrderBy.Desc ? 'asc' : 'desc';
  const { isDistrictAdmin, isSchoolAdmin } = useRole();
  const [anchorEls, setAnchorEls] = useState<Record<string, HTMLElement | null>>({});

  const handlePopoverOpen = (event: React.MouseEvent, rowId: string) => {
    setAnchorEls((prevAnchorEls) => ({
      ...prevAnchorEls,
      [rowId]: event.currentTarget as HTMLElement,
    }));
  };

  const handlePopoverClose = (rowId: string) => {
    setAnchorEls((prevAnchorEls) => {
      const updatedAnchorEls = { ...prevAnchorEls };
      delete updatedAnchorEls[rowId];
      return updatedAnchorEls;
    });
  };

  const getTeacher = (teachers?: Array<TeacherInfo | undefined | null>) => {
    teachers?.sort((t1, t2) => Number(!t1?.primary_teacher) - Number(!t2?.primary_teacher));
    return (teachers && teachers?.length > 0 ? `${teachers[0]?.primary_teacher ? '*' : ''}${teachers?.[0]?.teacher_name} ${teachers?.length > 1 ? `+${teachers?.length - 1}` : ''}` : '-');
  };

  const sortColumn = (title:string, sortEnum: ClassesSort) => (
    <StyledTableSort
      direction={orderByIcon}
      onClick={handleSort(sortEnum)}
      active={sort === sortEnum}
      IconComponent={ArrowDownIcon}
    >
      { title }
    </StyledTableSort>
  );

  return (
    <Box
      display="grid"
      width="100%"
      mt={marginTop}
    >
      <Box>
        <StyledContainerRoot sx={ sxProps ?? {} }>
          <Table
            stickyHeader
            aria-label="students assignments table"
            sx={{ ...((loading || isTableEmpty) && tableWithNoDataStyles.noData) }}
          >
            <TableHead>
              <TableRow>
                <StyledTableCell sx={{ textAlign: 'left', width: '25%' }}>
                  { sortColumn('Class', ClassesSort.Name) }
                </StyledTableCell>
                <StyledTableDataCellWidth10>
                  Teacher
                </StyledTableDataCellWidth10>
                <StyledTableDataCellWidth10>
                  { sortColumn('Period', ClassesSort.Periods) }
                </StyledTableDataCellWidth10>
                <StyledTableDataCellWidth10>
                  Grades
                </StyledTableDataCellWidth10>
                <StyledTableDataCellWidth10>
                  Custom
                </StyledTableDataCellWidth10>
                <StyledTableDataCellWidth10>
                  Active PT
                </StyledTableDataCellWidth10>
                <StyledTableDataCell>
                  { sortColumn('School', ClassesSort.School) }
                </StyledTableDataCell>
                <StyledTableDataCellWidth10>
                  { sortColumn('District', ClassesSort.District) }
                </StyledTableDataCellWidth10>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && classesData?.map((row) => (
                <TableRow key={row.id}>
                  <StyledTableCellOverFlowContent>
                    <Typography
                      component={Link}
                      variant='redirectionLinks'
                      to={`/admin-classes/${row.id}`}
                    >
                      {row.name}
                    </Typography>
                  </StyledTableCellOverFlowContent>
                  {row.teachers && row?.teachers?.length > 1 ?
                    <StyledTableDataColumn >
                      <Box  >
                        <Typography component="span" variant='redirectionLinks' onMouseOver={(event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => handlePopoverOpen(event, row?.id)} >
                          {getTeacher(row?.teachers)}
                        </Typography>
                        <Popover
                          anchorEl={anchorEls[row?.id]}
                          open={Boolean(anchorEls[row?.id])}
                          onClose={() => handlePopoverClose(row?.id)}
                          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                          sx={{
                            '&.MuiPopover-root .MuiPopover-paper': {
                              borderRadius: '8px',
                              border: '2px solid',
                              borderColor: palette.customGrey.adminButtonBorder,
                              padding: '8px',
                            },

                          }}

                        >
                          <Box  >
                            {row?.teachers?.map((teacher, i) =>
                              <Box padding={'8px'}   >
                                <Typography
                                  key={i * Math.random()}
                                  component={Link}
                                  variant='redirectionLinks'
                                  sx={{ mb: '6px' }}
                                  to={`/teachers/${teacher?.id}`}
                                  onClick={() => handlePopoverClose(row?.id)}
                                >
                                  {teacher?.primary_teacher && '*'}
                                  {teacher?.teacher_name}
                                </Typography>
                              </Box>)}
                          </Box>
                        </Popover>
                      </Box>
                    </StyledTableDataColumn>
                    :
                    <StyledTableDataColumn>
                      <Typography
                        component={Link}
                        variant='redirectionLinks'
                        to={`/teachers/${row?.teachers[0]?.id}`}
                      >
                        {getTeacher(row?.teachers)}
                      </Typography>
                    </StyledTableDataColumn>
                  }
                  <StyledTableDataColumn>
                    {row?.period || '-'}
                  </StyledTableDataColumn>
                  <StyledTableDataColumn>
                    {row?.grades?.join() || '-'}
                  </StyledTableDataColumn>
                  <StyledTableDataColumn>
                    {row?.category === SectionCategory.CustomClass
                      ? <SuccessIcon sx={{ ...successIconStyles.customClassIcon }} /> : '-'}
                  </StyledTableDataColumn>
                  <StyledTableDataColumn>
                    {row?.active_pt ? <SuccessIcon /> : '-'}
                  </StyledTableDataColumn>
                  <StyledTableDataColumn>
                    <Typography
                      component={Link}
                      variant='redirectionLinks'
                      to={isSchoolAdmin ? '/schools/myschool' : `/schools/${row.school_id}`}
                    >
                      {row?.school || '-'}
                    </Typography>
                  </StyledTableDataColumn>
                  <StyledTableDataColumn sx={{ fontWeight: 800 }}>
                    <Typography
                      component={Link}
                      variant='redirectionLinks'
                      to={isDistrictAdmin || isSchoolAdmin ? '/districts/my-district' : `/districts/${row.district_id}`}
                    >
                      {row?.district || '-'}
                    </Typography>
                  </StyledTableDataColumn>
                </TableRow>
              ))}
              {isTableEmpty && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                  >
                    <NoResults
                      label="No Classes"
                      description="There has yet to be a class created."
                      svgComponent={<WavingLadyFrame />}
                    />
                  </TableCell>
                </TableRow>
              )}
              {loading && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                  >
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledContainerRoot>
      </Box>
      <CustomPagination
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        page={pagination.page}
        onChange={handlePageChange}
        setRowsPerPage={setRowsPerPage}
      />
    </Box>
  );
};
export default List;
